import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { hypothesesApi } from '@/services/api/hypotheses'
import type {
  HypothesisCreateBody,
  HypothesisPatchBody,
  HypothesisStatus,
} from '@/types/hypothesis'

// --- Query keys ---

export const hypothesisKeys = {
  all: ['hypotheses'] as const,
  list: (caseId: string, statuses?: HypothesisStatus[]) =>
    ['hypotheses', 'list', caseId, statuses ?? []] as const,
  detail: (caseId: string, id: string) => ['hypotheses', 'detail', caseId, id] as const,
  runs: (caseId: string, id: string) => ['hypotheses', id, 'runs', caseId] as const,
  run: (caseId: string, id: string, runId: string) =>
    ['hypotheses', id, 'runs', caseId, runId] as const,
}

// --- Queries ---

export function useHypotheses(caseId: string, statuses?: HypothesisStatus[]) {
  return useQuery({
    queryKey: hypothesisKeys.list(caseId, statuses),
    queryFn: () => hypothesesApi.list(caseId, statuses),
    enabled: !!caseId,
  })
}

export function useHypothesis(caseId: string, hypothesisId: string | undefined) {
  return useQuery({
    queryKey: hypothesisId
      ? hypothesisKeys.detail(caseId, hypothesisId)
      : ['hypotheses', 'detail', 'none'],
    queryFn: () => hypothesesApi.get(caseId, hypothesisId as string),
    enabled: !!caseId && !!hypothesisId,
  })
}

/** Single-run poll — refetches while the run is active so the chat research
 * card flips from live panel to report without an SSE dependency. */
export function useHypothesisRun(
  caseId: string,
  hypothesisId: string | undefined,
  runId: string | undefined
) {
  return useQuery({
    queryKey:
      hypothesisId && runId
        ? hypothesisKeys.run(caseId, hypothesisId, runId)
        : ['hypotheses', 'run', 'none'],
    queryFn: () => hypothesesApi.getRun(caseId, hypothesisId as string, runId as string),
    enabled: !!caseId && !!hypothesisId && !!runId,
    // Chat history can mount many research cards in one render storm; without
    // staleTime every remount refires the same GET (observed: ~30 identical
    // requests in 2s on page load). Interval refetches below ignore staleness,
    // so the active-run poll cadence is unaffected. Finished runs are
    // immutable — never refetch them.
    staleTime: query => {
      const status = query.state.data?.status
      return status === 'queued' || status === 'running' || status === undefined
        ? 10_000
        : Number.POSITIVE_INFINITY
    },
    refetchInterval: query => {
      const status = query.state.data?.status
      return status === 'queued' || status === 'running' ? 2500 : false
    },
  })
}

export function useHypothesisRuns(caseId: string, hypothesisId: string | undefined) {
  return useQuery({
    queryKey: hypothesisId
      ? hypothesisKeys.runs(caseId, hypothesisId)
      : ['hypotheses', 'runs', 'none'],
    queryFn: () => hypothesesApi.listRuns(caseId, hypothesisId as string),
    enabled: !!caseId && !!hypothesisId,
  })
}

// --- Mutations ---

export function useCreateHypothesis(caseId: string) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (body: HypothesisCreateBody) => hypothesesApi.create(caseId, body),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['hypotheses', 'list', caseId] })
    },
  })
}

export function useUpdateHypothesis(caseId: string) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ id, patch }: { id: string; patch: HypothesisPatchBody }) =>
      hypothesesApi.update(caseId, id, patch),
    onSuccess: hyp => {
      qc.invalidateQueries({ queryKey: ['hypotheses', 'list', caseId] })
      qc.invalidateQueries({ queryKey: hypothesisKeys.detail(caseId, hyp.id) })
    },
  })
}

export function useDeleteHypothesis(caseId: string) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (id: string) => hypothesesApi.remove(caseId, id),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['hypotheses', 'list', caseId] })
    },
  })
}

export function useTriggerHypothesisRun(caseId: string) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (hypothesisId: string) => hypothesesApi.triggerRun(caseId, hypothesisId),
    onSuccess: (_run, hypothesisId) => {
      qc.invalidateQueries({ queryKey: ['hypotheses', 'list', caseId] })
      qc.invalidateQueries({ queryKey: hypothesisKeys.detail(caseId, hypothesisId) })
      qc.invalidateQueries({ queryKey: hypothesisKeys.runs(caseId, hypothesisId) })
    },
  })
}
